/**
 * Build-mode ticket / epic @-reference serialization.
 *
 * Mirrors utils/cabTagSerializer for the CAB chat feature, but references
 * tickets by their sequential id (e.g. "TKT-001") and epics by their Mongo id.
 *
 * At send time, `@<label>` text is rewritten to a `{{ticket:TKT-001|<label>}}`
 * or `{{epic:<epicId>|<label>}}` token, which the backend resolves into a
 * readable label + a context block for the build agent.
 */

export interface TicketTag {
  id: string;
  label: string;
  /** Present for ticket references (e.g. "TKT-001"). */
  ticketId?: string;
  /** 'ticket' (default) or 'epic'. */
  type?: 'ticket' | 'epic';
  /** Present for epic references (the epic's Mongo id). */
  epicId?: string;
}

export const TICKET_TAG_PATTERN = /\{\{ticket:([^|]+)\|([^}]+)\}\}/g;
export const EPIC_TAG_PATTERN = /\{\{epic:([^|]+)\|([^}]+)\}\}/g;

/** Replace each `@<label>` occurrence with its `{{ticket|epic:id|label}}` token. */
export function serializeTicketTagsIntoContent(content: string, tags: TicketTag[]): string {
  if (!tags.length) return content;

  let result = content;
  for (const tag of tags) {
    const tagMarker = `@${tag.label}`;
    const serialized = tag.type === 'epic'
      ? `{{epic:${tag.epicId || tag.id}|${tag.label}}}`
      : `{{ticket:${tag.ticketId || tag.id}|${tag.label}}}`;
    result = result.replace(tagMarker, serialized);
  }
  return result;
}

/** Convert serialized tokens back to readable `@<label>` text (for display). */
export function stripTicketTagMarkers(content: string): string {
  return content
    .replace(TICKET_TAG_PATTERN, '@$2')
    .replace(EPIC_TAG_PATTERN, '@$2');
}
